// ============================================================================
// GsBreadcrumb — fil d'Ariane location-based multi-niveau (UI_STANDARDS 4c)
// ============================================================================
// Wayfinding neutre (slate, JAMAIS brand) : le fil reflete la POSITION de la
// page dans la hierarchie informationnelle, pas l'historique du navigateur.
// Domaine-agnostique : libelles + ariaLabel par props (JAMAIS de t() dans
// @gs/ui). Navigation agnostique routeur : LinkComponent > onNavigate > span.
//
// Responsive : mobile etroit = parent DIRECT (`< [parent]`) + courant ;
// desktop = trail complet (segments intermediaires visibles sm+).

import { ChevronRight } from 'lucide-react';

export interface GsBreadcrumbItem {
  label: string;
  /** Absent sur le dernier item (= page courante, non cliquable). */
  to?: string;
}

export interface GsBreadcrumbProps {
  items: GsBreadcrumbItem[];
  /** aria-label du <nav> (i18n par prop — jamais de t() dans @gs/ui). */
  ariaLabel: string;
  onNavigate?: (to: string) => void;
  LinkComponent?: React.ComponentType<{
    to: string;
    className?: string;
    children: React.ReactNode;
  }>;
  className?: string;
}

const LINK_CLASS =
  'touch-manipulation font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300';
const CURRENT_CLASS = 'truncate font-semibold text-slate-800 dark:text-slate-100';
const SEP_CLASS = 'shrink-0 text-slate-300 dark:text-slate-600';

function CrumbLink({
  to,
  className,
  children,
  LinkComponent,
  onNavigate,
}: {
  to: string;
  className: string;
  children: React.ReactNode;
  LinkComponent?: GsBreadcrumbProps['LinkComponent'];
  onNavigate?: (to: string) => void;
}) {
  if (LinkComponent) {
    return (
      <LinkComponent to={to} className={className}>
        {children}
      </LinkComponent>
    );
  }
  if (onNavigate) {
    return (
      <button type="button" className={className} onClick={() => onNavigate(to)}>
        {children}
      </button>
    );
  }
  return <span className={className}>{children}</span>;
}

export function GsBreadcrumb({
  items,
  ariaLabel,
  onNavigate,
  LinkComponent,
  className,
}: GsBreadcrumbProps) {
  if (items.length === 0) return null;

  const current = items[items.length - 1]!;
  const parent = items.length >= 2 ? items[items.length - 2] : undefined;

  return (
    <nav
      aria-label={ariaLabel}
      className={['mb-2 text-meta text-slate-500', className].filter(Boolean).join(' ')}
    >
      {/* Mobile etroit : `< [parent direct]` + courant */}
      <ol className="flex items-center gap-1.5 sm:hidden">
        {parent ? (
          <li className="flex min-w-0 items-center gap-1.5">
            {parent.to ? (
              <CrumbLink
                to={parent.to}
                className={`${LINK_CLASS} truncate`}
                LinkComponent={LinkComponent}
                onNavigate={onNavigate}
              >
                <span aria-hidden="true">&lt; </span>
                {parent.label}
              </CrumbLink>
            ) : (
              <span className="truncate font-medium text-slate-500">
                <span aria-hidden="true">&lt; </span>
                {parent.label}
              </span>
            )}
            <ChevronRight size={14} aria-hidden="true" className={SEP_CLASS} />
          </li>
        ) : null}
        <li className={CURRENT_CLASS} aria-current="page">
          {current.label}
        </li>
      </ol>

      {/* Desktop / sm+ : trail complet */}
      <ol className="hidden items-center gap-1.5 sm:flex">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1.5">
              {index > 0 ? (
                <ChevronRight size={14} aria-hidden="true" className={SEP_CLASS} />
              ) : null}
              {isLast || !item.to ? (
                <span
                  className={isLast ? CURRENT_CLASS : 'truncate font-medium text-slate-500'}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              ) : (
                <CrumbLink
                  to={item.to}
                  className={`${LINK_CLASS} truncate`}
                  LinkComponent={LinkComponent}
                  onNavigate={onNavigate}
                >
                  {item.label}
                </CrumbLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
